import React from 'react';
import styled from 'styled-components';
import { 
  FaCalendarAlt, 
  FaFlagCheckered, 
  FaUsers, 
  FaInfoCircle, 
  FaExclamationTriangle,
  FaTrophy,
  FaWeightHanging,
  FaGavel 
} from 'react-icons/fa';
import { theme } from '../../theme';

// Breakpoints
const breakpoints = {
  mobile: '768px',
  tablet: '1024px'
};

// Componentes de UI
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  color: ${theme.colors.text.primary};
  line-height: 1.6;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 8px;
    margin: 0;
    max-width: 100%;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 3rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${theme.colors.divider};
  
  @media (max-width: ${breakpoints.mobile}) {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${theme.colors.primary.main};
  margin-bottom: 1rem;
  font-weight: 700;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.8rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: ${theme.colors.text.secondary};
  max-width: 800px;
  margin: 0 auto;
`;

const Section = styled.section`
  margin-bottom: 3rem;
  background: ${theme.colors.background.paper};
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    margin-bottom: 1.5rem;
    padding: 1rem;
    border-radius: 8px;
    
    &:hover {
      transform: none;
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  color: ${theme.colors.primary.main};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    color: ${theme.colors.secondary.main};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.4rem;
    margin-bottom: 1rem;
    gap: 0.5rem;
  }
`;

const Content = styled.div`
  p {
    margin-bottom: 1.25rem;
    font-size: 1.1rem;
    line-height: 1.7;
  }
  
  ul, ol {
    margin: 1.5rem 0;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.75rem;
      
      &::marker {
        color: ${theme.colors.primary.main};
        font-weight: bold;
      }
    }
  }
  
  h3 {
    font-size: 1.5rem;
    color: ${theme.colors.primary.dark};
    margin: 2rem 0 1rem;
  }
`;

const Highlight = styled.span`
  background: ${theme.colors.secondary.light};
  color: ${theme.colors.secondary.dark};
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
`;

interface ImportantNoteProps {
  $type?: 'info' | 'warning' | 'error';
  children: React.ReactNode;
}

const ImportantNote = styled.div<ImportantNoteProps>`
  background: ${({ $type = 'info' }) => 
    $type === 'error' 
      ? 'rgba(246, 78, 96, 0.15)' 
      : $type === 'warning'
      ? 'rgba(255, 168, 0, 0.15)'
      : 'rgba(54, 153, 255, 0.15)'};
  border-left: 4px solid ${({ $type = 'info' }) => 
    $type === 'error' 
      ? theme.colors.error.main 
      : $type === 'warning'
      ? '#FFA000'
      : theme.colors.info.main};
  padding: 1rem 1.5rem;
  border-radius: 4px;
  margin: 1.5rem 0;
  display: flex;
  gap: 0.75rem;
  color: ${theme.colors.text.primary};
  
  svg {
    color: ${({ $type = 'info' }) => 
      $type === 'error' 
        ? theme.colors.error.main 
        : $type === 'warning'
        ? '#FFA000'
        : theme.colors.info.main};
    font-size: 1.5rem;
    flex-shrink: 0;
    margin-top: 0.2rem;
  }
  
  div {
    flex: 1;
  }
  
  strong {
    color: ${({ $type = 'info' }) => 
      $type === 'error' 
        ? theme.colors.error.main 
        : $type === 'warning'
        ? '#FFA000'
        : theme.colors.info.main};
  }
`;

// Componentes de tablas
const PointsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  background: ${theme.colors.background.paper};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  
  th, td {
    padding: 0.75rem 1rem;
    text-align: center;
    border: 1px solid ${theme.colors.divider};
  }
  
  th {
    background-color: ${theme.colors.primary.main};
    color: white;
    font-weight: 600;
  }
  
  tr:nth-child(even) {
    background-color: ${theme.colors.background.default};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.9rem;
    
    th, td {
      padding: 0.5rem;
    }
  }
`;

const LastresTable = styled(PointsTable)`
  max-width: 300px;
  margin: 1.5rem 0;
  
  th, td {
    text-align: center;
  }
`;

const GuiaBienvenida: React.FC = () => {
  return (
    <Container>
      <Header>
        <Title>Guía de Bienvenida</Title>
        <Subtitle>Todo lo que necesitas saber para comenzar tu aventura en VR Simracers</Subtitle>
      </Header>
      
      <Section>
        <SectionTitle>
          <FaCalendarAlt />
          ¿Cómo son las Temporadas en VRS?
        </SectionTitle>
        <Content>
          <p>
            A lo largo del año organizamos 3 temporadas diferentes (Otoño, Invierno y Primavera). 
            Durante el verano organizamos pachangas pero no tenemos liga como tal.
          </p>
          
          <p>
            Antes de empezar cada temporada, realizamos jornadas clasificatorias para conocer el coche 
            elegido y medir nuestro nivel al respecto del resto de compañeros de la comunidad. Estas 
            jornadas definirán en qué categoría correrá cada piloto. Los socios que entren con la 
            temporada ya empezada se unirán a la liga más baja que exista.
          </p>
          
          <p>
            Una vez terminadas las jornadas clasificatorias, se asignará a cada participante a un equipo, 
            con su jefe de equipo correspondiente, que nos ayudará en todo lo necesario en estos primeros 
            pasos y al que podremos recurrir en caso de duda.
          </p>
          
          <p>
            La temporada Primavera 2025 constará de 6 carreras.
          </p>
          
          <ImportantNote $type="info">
            <FaInfoCircle />
            <div>
              <strong>Importante:</strong> Como regla general, las carreras de la categoría Pro se disputarán 
              los Jueves y las de la categoría Advance justo un día antes (Miércoles). Debido a fechas 
              complicadas (festivos, vacaciones,…) se podrán poner en días diferentes a los habituales, 
              avisando de esta circunstancia en los canales habituales. Las carreras se disputarán cada 2 semanas.
            </div>
          </ImportantNote>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>
          <FaFlagCheckered />
          Entrenamientos
        </SectionTitle>
        <Content>
          <p>
            Los servidores de cada liga estarán disponibles 24h/7d para que podáis entrenar cuando y como queráis.
          </p>
          
          <p>
            Es más que recomendable estar conectado a algún canal de <a href="https://discord.gg/j67pCm2" target="_blank" rel="noopener noreferrer"><Highlight>Discord</Highlight></a> de la comunidad 
            durante los entrenamientos, por si tenemos que contactaros mientras estáis rodando (aunque no tengáis 
            el micrófono activo).
          </p>
          
          <ImportantNote $type="warning">
            <FaExclamationTriangle />
            <div>
              <strong>Atención:</strong> Si vais a ausentaros durante un rato del simulador y luego volver, 
              no dejéis vuestro coche conectado al servidor, ya que puede impedir al resto de compañeros 
              el saltar a las diferentes sesiones.
            </div>
          </ImportantNote>
          
          <p>
            <strong>Es obligatorio dar un mínimo de 20 vueltas antes de cada carrera</strong> en los servidores 
            de entrenamiento para poder participar.
          </p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>
          <FaFlagCheckered />
          Carreras
        </SectionTitle>
        <Content>
          <p>
            Los días de carrera, deberemos estar conectados obligatoriamente al canal correspondiente de Discord a las 22:40h para asistir al briefing de cada carrera, donde se explicarán las peculiaridades de la misma. 
            <Highlight>Está terminantemente prohibido estar rodando en los servidores durante el briefing.</Highlight>
          </p>
          
          <h3>Calendario de la Sesión</h3>
          <ul>
            <li><strong>22:40h</strong> - Briefing en Discord (obligatorio)</li>
            <li><strong>23:00h</strong> - Inicio de la clasificación</li>
            <li>Al finalizar la qualy, el servidor saltará automáticamente a la sesión de carrera</li>
            <li>2 minutos de espera antes de la salida de la carrera</li>
          </ul>
          
          <ImportantNote $type="warning">
            <FaExclamationTriangle />
            <div>
              <strong>Atención:</strong> El uso de escape para ir a Box está permitido en la clasificación, pero no en carrera. 
              Si en carrera sufres un accidente o desconexión que te mande a boxes, no podrás volver a salir a pista.
            </div>
          </ImportantNote>
          
          <h3>Reglas de Pits</h3>
          <ul>
            <li>Los coches en el carril de pits son transparentes para evitar colisiones</li>
            <li>Límite de velocidad en pits: 80 km/h (activación automática)</li>
            <li>Entrar a más de 80 km/h resultará en un Drive-Through</li>
            <li>Respetar las líneas que delimitan el carril de boxes</li>
          </ul>
          
          <h3>Sistema de Penalizaciones</h3>
          <p>Utilizamos el addon Real Penalty que controla los cortes de curvas:</p>
          <ul>
            <li>5 cortes permitidos por carrera (puede variar según el evento)</li>
            <li>6º corte: Drive-Through obligatorio</li>
            <li>Si Real Penalty no detecta un corte, se aplica la regla de 2 ruedas en el asfalto</li>
          </ul>
          
          <ImportantNote $type="info">
            <FaInfoCircle />
            <div>
              <strong>Importante:</strong> Los Drive-Through deben cumplirse en las 2 vueltas siguientes. 
              Si no se cumple, el servidor te expulsará. Si es en la última vuelta, se añadirán 20 segundos a tu tiempo final.
            </div>
          </ImportantNote>
          
          <h3>Normas de Adelantamiento</h3>
          <ul>
            <li>Bandera azul: Debes facilitar el adelantamiento al piloto que te va a doblar</li>
            <li>El piloto que adelanta debe esperar a una situación segura</li>
            <li>En curvas: Se considera adelantado cuando el piloto que adelanta tiene su eje delantero alineado con el trasero del otro coche</li>
            <li>Solo se permite un cambio de dirección para defender la posición</li>
          </ul>
          
          <h3>Reincorporaciones</h3>
          <p>
            Si te sales de pista, debes reincorporarte de forma segura sin molestar a otros competidores. 
            Si te quedas parado en pista, no te muevas hasta que todos los competidores hayan pasado.
          </p>
          
          <ImportantNote $type="error">
            <FaExclamationTriangle />
            <div>
              <strong>¡Atención!</strong> Al finalizar la carrera, asegúrate de tener suficiente combustible para volver a boxes. 
              Quedarte sin combustible resultará en descalificación.
            </div>
          </ImportantNote>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>
          <FaTrophy />
          Puntuaciones Individuales
        </SectionTitle>
        <Content>
          <h3>Sistema de Puntos (Carreras 1-5)</h3>
          <PointsTable>
            <thead>
              <tr>
                <th>Posición</th><th>Puntos</th>
                <th>Posición</th><th>Puntos</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(12)].map((_, i) => (
                <tr key={i}>
                  <td>{i + 1}º</td><td>{24 - i}</td>
                  <td>{i + 13}º</td><td>{12 - i > 0 ? 12 - i : 1}</td>
                </tr>
              ))}
            </tbody>
          </PointsTable>
          
          <h3>Sistema de Puntos (Carrera 6 - Evento Especial)</h3>
          <PointsTable>
            <thead>
              <tr>
                <th>Posición</th><th>Puntos</th>
                <th>Posición</th><th>Puntos</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(12)].map((_, i) => (
                <tr key={i}>
                  <td>{i + 1}º</td><td>{60 - (i * 3)}</td>
                  <td>{i + 13}º</td><td>{i < 9 ? 24 - (i * 3) : 0}</td>
                </tr>
              ))}
            </tbody>
          </PointsTable>
          
          <p>
            <strong>Puntos adicionales:</strong> +1 punto para el piloto con la vuelta más rápida de la carrera.
          </p>
          
          <p>
            <strong>Descarte:</strong> Al final de la temporada, se descartará el peor resultado de cada piloto (excepto si es inferior a -2 puntos).
          </p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>
          <FaUsers />
          Puntuaciones por Equipos
        </SectionTitle>
        <Content>
          <p>
            Además de la clasificación individual, se lleva un cómputo del resultado por equipos que determina la clasificación final.
          </p>
          
          <h3>Liga Primavera 2025</h3>
          <p>
            En cada carrera, cada equipo sumará los puntos de sus 3 pilotos con mejor resultado.
          </p>
          
          <p>
            Estos resultados también determinan las reducciones de lastre para los equipos en la siguiente carrera (ver apartado Lastres).
          </p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>
          <FaWeightHanging />
          Sistema de Lastres
        </SectionTitle>
        <Content>
          <p>
            Utilizamos un sistema de lastres para equilibrar la competición. Los lastres se aplican según la posición en el campeonato:
          </p>
          
          <LastresTable>
            <thead>
              <tr>
                <th>Posición</th>
                <th>Lastre</th>
              </tr>
            </thead>
            <tbody>
              {[40, 36, 32, 28, 24, 20, 16, 12, 8, 4].map((kg, i) => (
                <tr key={i}>
                  <td>{i + 1}º</td>
                  <td>{kg} kg</td>
                </tr>
              ))}
            </tbody>
          </LastresTable>
          
          <h3>Aplicación de Lastres</h3>
          <ul>
            <li><strong>1ª Carrera:</strong> Sin lastres</li>
            <li><strong>2ª y 3ª Carreras:</strong> Según puntos totales acumulados</li>
            <li><strong>4ª a 6ª Carreras:</strong> Según puntos totales, descartando el peor resultado (excepto si es inferior a -2 puntos)</li>
          </ul>
          
          <h3>Reducción de Lastre por Equipo</h3>
          <p>Se aplica a partir de la 2ª carrera según la posición del equipo en la carrera anterior:</p>
          <PointsTable>
            <thead>
              <tr><th>Posición Equipo</th><th>Reducción de Lastre</th></tr>
            </thead>
            <tbody>
              <tr><td>1º</td><td>-7 kg</td></tr>
              <tr><td>2º</td><td>-4 kg</td></tr>
              <tr><td>3º</td><td>-2 kg</td></tr>
              <tr><td>4º</td><td>-1 kg</td></tr>
              <tr><td>5º o inferior</td><td>0 kg</td></tr>
            </tbody>
          </PointsTable>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>
          <FaGavel />
          Sistema de Reclamaciones
        </SectionTitle>
        <Content>
          <p>
            Para garantizar la limpieza deportiva, contamos con un sistema de reclamaciones:
          </p>
          
          <h3>Proceso de Reclamación</h3>
          <ol>
            <li>La organización anunciará cuándo se abre el plazo de reclamaciones</li>
            <li>El piloto que desee reclamar debe rellenar el formulario correspondiente</li>
            <li>No es necesario adjuntar vídeo en la reclamación inicial</li>
            <li>El piloto reclamado podrá presentar sus alegaciones</li>
          </ol>
          
          <ImportantNote $type="info">
            <FaInfoCircle />
            <div>
              <strong>Importante:</strong> Los comisarios revisarán cada incidente utilizando las repeticiones oficiales 
              desde la cámara interior de todos los coches implicados y desde 2 cámaras exteriores. La resolución será inapelable.
            </div>
          </ImportantNote>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>
          <FaGavel />
          Tabla de Sanciones
        </SectionTitle>
        <Content>
          <PointsTable>
            <thead>
              <tr>
                <th>Leves</th>
                <th>Medias</th>
                <th>Graves</th>
                <th>Muy Graves</th>
                <th>ANITDEPORTIVA</th>
                <th>ANTID. CON ACCIDENTE</th>
                <th>Otras</th>
              </tr>
              <tr>
                <th>WARNING</th>
                <th>-1 PT. EN LA GENERAL +W</th>
                <th>-2 PTS. EN LA GENERAL +W</th>
                <th>DT</th>
                <th>DT + DQ</th>
                <th>DT + DQ + NQ</th>
                <th>A DETERMINAR</th>
              </tr>
            </thead>
            <tbody>
              {/* Fila 1.1 - 1.7 */}
              <tr>
                <td>1.1<br />Pisar o rebasar las líneas de entrada o salida de box</td>
                <td>2.1<br />Ganar posición en una acción de doblaje sin devolver la misma.</td>
                <td>3.1<br />Incumplir bandera azul "ver normativa"</td>
                <td>4.1<br />Más de tres sanciones en la misma carrera o más de tres Warning en el campeonato</td>
                <td>5.1<br />No llevar operativas las apps obligatorias.</td>
                <td>6.1<br />Maniobrar después de accidente o trompo, cuando se está dentro de pista y vienen otros pilotos produciendo accidente</td>
                <td>7.1<br />Puntos a restar determinados por comisarios</td>
              </tr>
              
              {/* Fila 1.2 - 6.2 */}
              <tr>
                <td>1.2<br />Contacto peligroso o innecesario</td>
                <td>2.2<br />Accidente leve en todas sus variantes (con ganancia o pérdida de posición, con y sin daños)</td>
                <td>3.2<br />Accidente grave en todas sus variantes (con ganancia o pérdida de posición, con y sin daños)</td>
                <td>4.2<br />Accidente muy grave en todas sus variantes (con ganancia o pérdida de posición, con y sin daños)</td>
                <td>5.2<br />Mantener el coche en pista inutilizado por el motivo que sea más de 6 segundos</td>
                <td>6.2<br />Reincorporarse a pista cuando vienen pilotos cerca produciendo accidente</td>
                <td>7.2<br />No hacer la parada obligatoria en la ventana de pit.<br />-2 puntos + DT</td>
              </tr>
              
              {/* Fila 1.3 - 6.3 */}
              <tr>
                <td>1.3<br />Incumplimiento "cambio de trazada"</td>
                <td>2.3<br />Reiteración Incumplir "cambio de trazada"</td>
                <td>3.3<br />Ocasionar accidente en clasificación</td>
                <td>4.3<br />Incumplir las normas de clasificación</td>
                <td>5.3<br />Incorporarse a la carrera después de salir de la misma con Escape u otros motivos, o una vez comenzada la misma.</td>
                <td>6.3<br />Provocar accidente por culpa de lag u otros motivos técnicos ajenos a la organización</td>
                <td>7.3<br />Agravante por incidente en primera vuelta.<br />Incremento de un nivel de sanción</td>
              </tr>
              
              {/* Fila 1.4 - 6.4 */}
              <tr>
                <td>1.4<br />Incumplimiento de las normas de bandera amarilla</td>
                <td>2.4<br />Incumplir normas bandera amarilla provocando accidente leve</td>
                <td>3.4<br />Reincorporarse a pista cuando vienen pilotos cerca (se les puede ver en mapa) sin producir accidente</td>
                <td>4.4<br />Maniobrar después de accidente o trompo, cuando se está dentro de pista y vienen otros pilotos</td>
                <td>5.4<br />Maniobrar marcha atrás para reincorporarse a pista.</td>
                <td>6.4<br />Reincorporarse o maniobrar para reincorporarse a pista marcha atrás produciendo accidente</td>
                <td>7.4<br />Accidente en la primera curva con daños.<br />Sanción mínima: – 2 ptos + W + DT</td>
              </tr>
              
              {/* Fila 1.5 - 6.5 */}
              <tr>
                <td>1.5<br />No finalizar la carrera en boxes conduciendo.</td>
                <td></td>
                <td></td>
                <td>4.5<br />DT recibido en última vuelta por PLP, sin posibilidad de cumplir en carrera.</td>
                <td>5.5<br />No cumplir DT de PitLanePenalty</td>
                <td>6.5<br />Dar marcha atrás para entrar a box, tanto en carrera como en clasificación, produciendo accidente</td>
                <td>7.5<br />Sanción no contemplada anteriormente a determinar por comisarios</td>
              </tr>
            </tbody>
          </PointsTable>
          
          <Section>
            <SectionTitle>Detalle de Sanciones</SectionTitle>
            <Content>
              <div className="info-card">
                <h3>Sanciones Medias</h3>
                <ul>
                  <li>Accidente a velocidad moderada, sin daños para los perjudicados, claramente evitable.</li>
                  <li>Accidente a velocidad moderada, con daños para los perjudicados.</li>
                  <li>Accidente a velocidad moderada, con pérdida de posición/posiciones para el perjudicado</li>
                  <li>Accidente a velocidad moderada, con pérdida de posición/posiciones para los perjudicados</li>
                  <li>Accidente a velocidad moderada, con pérdida de posición/posiciones para el perjudicado, más daños</li>
                </ul>
              </div>

              <div className="info-card">
                <h3>Sanciones Graves</h3>
                <ul>
                  <li>Accidente a gran velocidad, sin daños para los perjudicados, claramente evitable.</li>
                  <li>Accidente a gran velocidad, con daños para los perjudicados.</li>
                  <li>Accidente a gran velocidad, con pérdida de posición/posiciones para el perjudicado</li>
                  <li>Accidente a gran velocidad, con pérdida de posición/posiciones para los perjudicados</li>
                  <li>Accidente a gran velocidad, con pérdida de posición/posiciones para el perjudicado, más daños</li>
                  <li>Reincorporarse a pista cuando vienen pilotos cerca (entendiendo por cerca, cuando se les puede ver en el mapa) sin producir accidente</li>
                </ul>
              </div>

              <div className="info-card">
                <h3>Conducta Antideportiva</h3>
                <ul>
                  <li>No llevar operativas las apps obligatorias.</li>
                  <li>Provocar accidente por culpa del Lag, ya que se considera que es responsabilidad de cada uno, tomar las medidas oportunas para evitar o minimizar el lag, como revisar el router, no tener en el momento de la carrera más consumo del deseado, el apagado de aplicaciones o conexiones que puedan interferir, etc.</li>
                  <li>Salirse de carrera sin un motivo técnico que lo justifique (Gasolina, motor, PC, etc.)</li>
                  <li>No cumplir DT de pitlane Penalty</li>
                  <li>Reincorporarse o maniobrar para reincorporarse a pista marcha atrás</li>
                  <li>Incorporarse a la carrera después de ir a box, por cualquier medio que no sea entrar a box rodando con el coche</li>
                  <li>Reincorporarse a pista cuando vienen pilotos cerca produciendo accidente</li>
                  <li>Entrar al server en carrera una vez comenzada la carrera</li>
                  <li>No respetar cualquier orden de administración</li>
                  <li>Mantener el coche en pista inutilizado por más de 6 segundos</li>
                  <li>Dar marcha atrás para entrar a box, tanto en carrera o en clasificación</li>
                </ul>
                <div className="info-card-notice">
                  <p><strong style={{ color: 'black' }}>Conducta antideportiva:</strong> DQ (en esa carrera) + DT (Próxima carrera)</p>
                  <p><strong style={{ color: 'black' }}>Conducta antideportiva con accidente:</strong> DQ (en esa carrera) + No clasificar (próxima carrera) + DT (Próxima carrera)</p>
                </div>
              </div>

              <div className="info-card">
                <h3>Consideraciones Importantes</h3>
                <ul>
                  <li>Maniobrar, después de un accidente o trompo, cuando se está dentro de la pista y vienen otros pilotos, será penalizado con DT.</li>
                  <li>
                    <strong style={{ color: 'black' }}>DT en última vuelta:</strong> Se cumple en la siguiente carrera. En caso de ser la última del campeonato, los comisarios decidirán.
                  </li>
                  <li>
                    <strong style={{ color: 'black' }}>Entrar a pits (repostaje/ruedas) fuera de la ventana de tiempos:</strong>
                    <p>Incumplir esta norma implica una sanción de -2PTS + DT PRÓXIMA CARRERA.</p>
                  </li>
                  <li>
                    <strong style={{ color: 'black' }}>Accidente en la primera curva con daños:</strong>
                    <p>Las sanciones por causar un accidente en la primera curva pueden ser diferentes dependiendo de la causa y los perjuicios causados a terceros. Los comisarios podrán sancionar desde -2PTS + WARNING + 1 DT EN PRÓXIMA CARRERA hasta llegar incluso a la descalificación del piloto u otras más graves.</p>
                  </li>
                </ul>
                
                <div className="info-card-notice">
                  <p><strong style={{ color: 'black' }}>Incidentes no contemplados:</strong></p>
                  <p>Ante incidentes no contemplados en la normativa, los comisarios deliberarán y sancionarán en función de la gravedad.</p>
                </div>
              </div>

              <div className="info-card">
                <h3>Última Carrera de la Liga</h3>
                <ul>
                  <li><strong style={{ color: 'black' }}>DQ en última carrera:</strong> 0 puntos</li>
                  <li><strong style={{ color: 'black' }}>NQ en última carrera:</strong> -2 puntos</li>
                  <li><strong style={{ color: 'black' }}>DT en última carrera:</strong> -3 puntos</li>
                </ul>
              </div>
            </Content>
          </Section>
          
          <style>{`
            .info-card {
              background: rgba(54, 153, 255, 0.1);
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 24px;
              border-left: 4px solid #3699ff;
              box-shadow: 0 0 20px rgba(0,0,0,0.1);
              color: ${theme.colors.text.primary};
            }
            
            .info-card-notice {
              margin-top: 20px;
              padding: 15px;
              background: rgba(54, 153, 255, 0.08);
              border-radius: 6px;
              border-left: 3px solid #3699ff;
              color: ${theme.colors.text.primary};
            }
            
            .info-card h3 {
              color: #3699ff;
              margin-top: 0;
              margin-bottom: 16px;
              font-size: 1.25rem;
            }
            
            .info-card strong {
              color: ${theme.colors.text.primary};
            }
            
            .info-card li {
              margin-bottom: 8px;
              color: ${theme.colors.text.primary};
            }
            
            .info-card ul {
              padding-left: 20px;
              margin: 10px 0;
            }
            
            .info-card li {
              margin-bottom: 8px;
              line-height: 1.5;
            }
            
            .info-card strong {
              color: #fff;
            }
          `}</style>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>
          <FaInfoCircle />
          Preguntas Frecuentes
        </SectionTitle>
        <Content>
          <h3>¿Cómo puedo correr en VRS?</h3>
          <p>
            En primer lugar, debes darte de alta como usuario en web. Utiliza un nombre y apellido real. No nos gustan los nicks. 
            Una vez registrado, puedes instalar las apps que marcamos como obligatorias y recomendadas en la normativa específica 
            de cada liga: pitlanepenalty o realpenalty, helicrosa, etc. Si no sabes cómo instalar apps, te ayudaremos en el grupo.
          </p>
          
          <h3>¿Es obligatorio hacer un pago? ¿Cómo lo hago?</h3>
          <p>
            Absolutamente todas las ligas que organizamos en la comunidad están cerradas a socios. El cupo de socios es limitado 
            así que, tal vez alguna vez no esté disponible la opción de pago. Para pagar la cuota de socio deber ir a la página 
            principal de esta web y consultar las opciones de pago, también puedes ir al menú superior de esta web y en la sección 
            de "miembros" encontrarás la opción "quiero ser socio".
          </p>
          
          <h3>¿Qué es Telegram?</h3>
          <p>
            Telegram es una app de mensajería instantánea similar a WhatsApp. Es el núcleo de la comunidad. Instala la app en tu 
            móvil y también puedes hacerlo en tu PC. Allí nos podrás buscar como @vrsimracers y llegarás al grupo general de la comunidad. 
            Además de ese grupo tenemos otros más paralelos a los que podrás unirte después.
          </p>
          
          <h3>¿Tengo un dorsal?</h3>
          <p>
            Tu dorsal aparece inmediatamente tras registrarte en la web en la sección del menú principal "miembros / dorsales y pilotos". 
            Ese dorsal lo utilizaremos para hacer nuestros skins. Los Skins son las personalizaciones de nuestros coches.
          </p>
          
          <h3>Unificación de nombres ¿qué es eso?</h3>
          <p>
            El nombre con el que aparecerás dentro de la web cada vez que te apuntes a una carrera lo puedes cambiar desde el menú 
            principal de la web "miembros/mi usuario". Por favor, pon tu nombre y un apellido, al menos, para que podamos identificarte.
          </p>
          
          <h3>¿De dónde saco circuitos y coches?</h3>
          <p>
            En la comunidad utilizamos contenido base de Assetto Corsa, también utilizamos DLCs, que son packs de expansión del 
            simulador con algunos coches y circuitos (el contenido completo de Assetto Corsa está reunido en la versión llamada 
            "ultimate" y es habitual encontrarlo en webs de códigos por unos 10 euros). Otras veces utilizamos contenidos aportados 
            por modders. Estos mods de coches y circuitos los suministra la comunidad para que podamos utilizarlos.
          </p>
          
          <h3>¿Cuántas ligas hay? ¿Puedo elegir la liga a la que me apunto?</h3>
          <p>
            Dependiendo del número de inscritos tenemos varias ligas preparadas: PRO, ADVANCE, NOVEL, ROOKIE, NOVICE y BEGINNERS, 
            lo normal en la comunidad es encontrar participantes en las 3 primeras. La última liga puede tener un split de 22 pilotos 
            o varios splits. Se determina en cada trimestre.
          </p>
          
          <h3>¿Skins? ¿Lastres? ¿Equipos? ¿Puntuaciones?</h3>
          <p>
            En nuestra ligas los equipos van por sorteo. Siempre antes de temporada se unen cabezas de serie con los que no lo son, 
            también en los equipos se mezclan componentes de todas las categorías. Dependiendo del número de inscritos y de las ligas 
            celebradas, los equipos se conforman con 2, 3 o 4 pilotos elegidos por sorteo entre todos los participantes.
          </p>
          
          <h3>¿Premios?</h3>
          <p>
            En VRS por lo general no hay patrocinadores ni premios. Además, en las ocasiones en las que hemos dispuesto de algún regalo, 
            nunca se lo hemos otorgado al primero de cada liga, en su lugar, sorteamos estos premios entre los socios.
          </p>
          
          <h3>No tengo el nivel ¿me olvido de esto?</h3>
          <p>
            En absoluto. Ser buen piloto es ser limpio, no rápido. Limpio. Si cumples esa condición, nosotros te ubicaremos en la 
            categoría de tu nivel, sea el que sea. ¿Tardas 3 minutos en dar una vuelta completa a Spa? No importa, correrás con 
            gente que tiene tiempos muy aproximados a los tuyos, de esta manera te GARANTIZAMOS la diversión en pista.
          </p>
        </Content>
      </Section>
    </Container>
  );
};

export default GuiaBienvenida;
